import { cn } from '../../shared/lib/cn';
import type { TRealtimeStatus } from '../../shared/realtime';

const DOT: Record<TRealtimeStatus, string> = {
  live: 'bg-success',
  connecting: 'bg-motion',
  offline: 'bg-danger',
  unavailable: 'bg-suppressed',
  idle: 'bg-suppressed',
};

const WORD: Record<TRealtimeStatus, string> = {
  live: 'Live — new events announce themselves here',
  connecting: 'Opening the live stream…',
  offline: 'Live stream dropped — reconnecting',
  unavailable: 'No live stream. The list is what it loaded until you refresh it.',
  idle: 'Live stream off',
};

export interface ILiveBarProps {
  status: TRealtimeStatus;
  /** `null` means some arrived with the count unknown, which is not 0. */
  pending: number | null;
  onLoad: () => void;
  onReconnect: () => void;
}

export const LiveBar = ({ status, pending, onLoad, onReconnect }: ILiveBarProps) => {
  const waiting = pending === null || pending > 0;

  return (
    <div className="flex h-control-sm shrink-0 items-center gap-2 border-b border-border-sunken px-page text-meta text-faint">
      <span aria-hidden className={cn('size-dot shrink-0 rounded-full', DOT[status])} />
      <span className="truncate">{WORD[status]}</span>

      {(status === 'unavailable' || status === 'offline') && (
        <button
          type="button"
          onClick={onReconnect}
          className="shrink-0 text-accent underline underline-offset-2"
        >
          Try now
        </button>
      )}

      {!waiting && status !== 'live' && status !== 'connecting' && (
        <button
          type="button"
          onClick={onLoad}
          className="ml-auto flex h-control-xs shrink-0 items-center rounded-control bg-ghost px-inset font-medium text-primary hover:bg-hover-raised"
        >
          Refresh
        </button>
      )}

      {waiting && (
        <button
          type="button"
          onClick={onLoad}
          aria-live="polite"
          className="ml-auto flex h-control-xs shrink-0 items-center gap-2 rounded-control bg-ghost px-inset font-medium text-primary hover:bg-hover-raised"
        >
          {pending === null
            ? 'New events arrived'
            : `${pending} new ${pending === 1 ? 'event' : 'events'}`}
          <span className="text-faint">Load</span>
        </button>
      )}
    </div>
  );
};
